// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import { SubscriptionPlan } from '../home/subscription/subscription-plan.component';
import useAuth from '../auth/feature/auth';
import {
  EYE_PRODUCT_SLUG,
  useProductAccess
} from '../common/hooks/use-product-access';
import packageJson from '../../../package.json';
import eyePreview from '../../assets/images/squarebit-eye/eye_preview.png';
import refractionViews from '../../assets/images/squarebit-eye/refraction-views.png';
import webWireframeTq from '../../assets/images/squarebit-eye/web-wireframe-tq.png';
import webIrisDial from '../../assets/images/squarebit-eye/web-iris-dial.png';
import sheetMayaVp2 from '../../assets/images/squarebit-eye/sheet-maya-vp2.jpg';
import sheetUnreal57 from '../../assets/images/squarebit-eye/sheet-unreal-57.jpg';
import sheetUnreal58 from '../../assets/images/squarebit-eye/sheet-unreal-58.jpg';
import sheetBlenderCycles from '../../assets/images/squarebit-eye/sheet-blender-cycles.jpg';
import sheetBlenderGpu from '../../assets/images/squarebit-eye/sheet-blender-gpu.jpg';
import sheetThreejs from '../../assets/images/squarebit-eye/sheet-threejs.jpg';
import sheetOmniverse from '../../assets/images/squarebit-eye/sheet-omniverse.jpg';
import sheetHdstorm from '../../assets/images/squarebit-eye/sheet-hdstorm.jpg';

const CAPABILITIES = ['projection', 'rig', 'art'] as const;

/**
 * The card that runs wide. The Standby tiles vary their shape on purpose and
 * the page reads better for it; projection takes it because its still is a
 * three-up strip, which wants a landscape half rather than a letterboxed one.
 */
const WIDE_CARD: (typeof CAPABILITIES)[number] = 'projection';

/**
 * How many chips each capability card carries. The chips are the card in three
 * or four words, for readers who will not read the sentence -- the same
 * contract TouchPoseSection's TileChips keeps: a chip summarises the copy, it
 * never makes a claim the copy does not.
 */
const CAP_CHIPS: Record<(typeof CAPABILITIES)[number], number> = {
  projection: 4,
  rig: 4,
  art: 5
};

const CAP_MEDIA: Record<
  (typeof CAPABILITIES)[number],
  { src: string; altKey: string }
> = {
  projection: {
    src: refractionViews,
    altKey: 'squarebitEye.cap_projection_media_alt'
  },
  // Both of these are the live widget, captured rather than re-created: the
  // card should show the thing the visitor is about to scroll down and drive
  // themselves, not a lookalike of it.
  rig: { src: webWireframeTq, altKey: 'squarebitEye.cap_rig_media_alt' },
  art: { src: webIrisDial, altKey: 'squarebitEye.cap_art_media_alt' }
};

const HOSTS = [
  'Maya',
  'Arnold',
  'Unreal Engine',
  'Blender & Cycles',
  'three.js',
  'USD',
  'MaterialX',
  'Houdini Karma',
  'NVIDIA Omniverse',
  'Hydra Storm'
];

/**
 * The cross-host comparison sheets. Each is one host rendering the same eye
 * across six lighting setups and three views, so a sheet is already the parity
 * argument on its own; the carousel's job is only to get you through them.
 *
 * `pill` is the entry in HOSTS this sheet belongs to. Two sheets can share one
 * pill (Unreal 5.7 / 5.8, Blender Cycles / GPU) because the pill names the host
 * and the sheet names the renderer inside it -- which the sheet's own header
 * already says, so nothing needs to repeat it here.
 */
const SHEETS: {
  id: string;
  pill: string;
  src: string;
}[] = [
  { id: 'maya-vp2', pill: 'Maya', src: sheetMayaVp2 },
  { id: 'unreal-57', pill: 'Unreal Engine', src: sheetUnreal57 },
  { id: 'unreal-58', pill: 'Unreal Engine', src: sheetUnreal58 },
  { id: 'blender-cycles', pill: 'Blender & Cycles', src: sheetBlenderCycles },
  { id: 'blender-gpu', pill: 'Blender & Cycles', src: sheetBlenderGpu },
  { id: 'threejs', pill: 'three.js', src: sheetThreejs },
  { id: 'omniverse', pill: 'NVIDIA Omniverse', src: sheetOmniverse },
  { id: 'hdstorm', pill: 'Hydra Storm', src: sheetHdstorm }
];

const SHEET_MS = 3800;

/**
 * How long a click holds the carousel before it picks itself back up, then
 * carries on to the next sheet from wherever you put it. Same shape as the
 * widget's idle gaze wander, which pauses while you drag and resumes a few
 * seconds after you let go (IDLE_RESUME_MS in EyeWidget). Nine seconds was
 * long enough to read as stuck rather than paused.
 */
const RESUME_MS = 3000;

/**
 * The card's category, above its heading. Plain blue text, NOT a pill: a pill
 * reads as something you can click, and this is a label. The chips below the
 * copy stay as they are, because they are a set and read as one.
 */
const CardLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-blue mb-2">
    {children}
  </div>
);

/** The capability card in three or four words. See CAP_CHIPS. */
const CapChips: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="flex flex-wrap gap-[6px] mt-4 p-0 m-0 list-none">
    {items.map((label) => (
      <li
        key={label}
        className="rounded-full border border-border-dark/[0.14] px-[9px] py-[3px] text-[11px] font-medium leading-[1.5] text-content-muted-alt"
      >
        {label}
      </li>
    ))}
  </ul>
);

/**
 * The host pills ARE the carousel's index. There is no second row of buttons:
 * a sheet plays, the pill for its host lights up, and clicking a pill jumps to
 * it. The four hosts with no sheet yet (Arnold, USD, MaterialX, Houdini Karma)
 * stay plain spans rather than dead buttons -- a pill that highlights invites a
 * click, so anything that cannot be clicked must not look like it can.
 */
const ParitySheets: React.FC = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [held, setHeld] = useState(false);
  const holdTimer = useRef<number>();

  /**
   * A click parks the carousel on that sheet, then hands it back. Picking a
   * pill is a request to look at one host, not a request to stop the thing
   * permanently, so the hold expires rather than latching. Clicking again
   * restarts the clock.
   */
  const pick = (idx: number) => {
    setActive(idx);
    setHeld(true);
    window.clearTimeout(holdTimer.current);
    holdTimer.current = window.setTimeout(() => {
      // Step on the way out, rather than releasing the hold and then waiting a
      // whole SHEET_MS for the next tick. Releasing alone left the picked sheet
      // up for RESUME_MS + SHEET_MS, nearly seven seconds, which reads as stuck
      // rather than held.
      setActive((v) => (v + 1) % SHEETS.length);
      setHeld(false);
    }, RESUME_MS);
  };

  useEffect(() => () => window.clearTimeout(holdTimer.current), []);

  useEffect(() => {
    // Honour reduced motion: hold sheet one rather than pulsing the page.
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches || held) return undefined;
    const id = window.setInterval(
      () => setActive((v) => (v + 1) % SHEETS.length),
      SHEET_MS
    );
    return () => window.clearInterval(id);
  }, [held]);

  const activePill = SHEETS[active].pill;

  return (
    <div className="mb-8">
      <div className="flex flex-wrap gap-2 mb-6">
        {HOSTS.map((host) => {
          const idx = SHEETS.findIndex((sh) => sh.pill === host);
          const base =
            'rounded-full border px-[14px] py-[6px] font-mono text-[12px] md:text-[13px] transition-colors';
          if (idx === -1) {
            /* No sheet for this host yet. It still answers a press, because a
               pill that does nothing at all reads as broken, but the press is
               the whole response: it greys under the finger and returns on
               release, and the carousel carries on from whatever sheet was
               already up.

               A span rather than a disabled button on purpose. :active fires on
               any element, so the press feedback costs nothing, while a button
               with aria-disabled announces itself to a screen reader as a
               control that is switched off -- which is the wrong story. There
               is no control here, only a label that acknowledges being poked. */
            return (
              <span
                key={host}
                className={`${base} select-none border-border-brand bg-surface-base text-content-muted-alt active:bg-content-muted/25 active:border-content-muted active:text-content-dark`}
              >
                {host}
              </span>
            );
          }
          const on = host === activePill;
          return (
            <button
              key={host}
              type="button"
              onClick={() => pick(idx)}
              aria-current={on}
              className={`${base} cursor-pointer ${
                on
                  ? 'border-blue bg-blue/10 text-blue-link'
                  : 'border-border-brand bg-surface-base text-content-muted-alt hover:text-content-dark active:bg-content-muted/20'
              }`}
            >
              {host}
            </button>
          );
        })}
      </div>

      {/* Nothing pauses this on hover. Hover-pause meant the carousel stopped
          wherever the pointer happened to rest, and on a touch screen it was
          worse than that: a tap synthesises mouseenter and never clears, so it
          latched on and the carousel stopped for good after a single step.
          Picking a host is the only hold, it lasts RESUME_MS, and then the
          loop continues from whatever you picked. Same behaviour on a mouse,
          a tablet and a phone. */}
      <div
        className="relative w-full overflow-hidden rounded-[14px] border border-border-brand bg-[#0d0d0f]"
        style={{ aspectRatio: '1600 / 903' }}
      >
        {SHEETS.map((sh, i) => (
          <img
            key={sh.id}
            src={sh.src}
            alt={t('squarebitEye.paritySheetAlt', { host: sh.pill })}
            loading={i === 0 ? 'eager' : 'lazy'}
            aria-hidden={i !== active}
            className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-500 ${
              i === active ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
      </div>

      <p className="mt-3 text-center text-content-muted-alt text-[12px] md:text-[13px] italic">
        {t('squarebitEye.paritySheetCaption')}
      </p>
    </div>
  );
};

const ASPECT_RATIO = '1919 / 999';

// Loaded lazily so three.js + the eye core stay out of the page's initial
// chunk; the section shows the still preview until it scrolls into view.
const EyeWidget = lazy(() => import('./EyeWidget'));

const supportsWebGL = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
};

/** True once the observed element has scrolled near the viewport (one-shot). */
function useInViewOnce<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);
  return [ref, inView];
}

const EYE_VERSION =
  (packageJson as { squarebitEye?: { version?: string } }).squarebitEye?.version ??
  '';

// The framed still. `loading` (the code chunk is downloading) blurs it and
// overlays the indeterminate load bar, so the load indicator is present the
// whole time and hands off seamlessly to the widget's own overlay. Without
// `loading` (WebGL unsupported) it's the sharp image + a quiet "Preview" tag.
const StillPreview: React.FC<{ variant?: 'poster' | 'loading' | 'static' }> = ({
  variant = 'poster'
}) => {
  const { t } = useTranslation();
  const loading = variant === 'loading';
  return (
    <div
      className="relative max-w-[1040px] mx-auto rounded-[16px] overflow-hidden border border-border-brand"
      style={{ aspectRatio: ASPECT_RATIO }}
      role={loading ? 'status' : undefined}
      aria-live={loading ? 'polite' : undefined}
      aria-busy={loading || undefined}
    >
      <img
        src={eyePreview}
        alt={t('squarebitEye.liveAriaLabel')}
        aria-hidden={loading || undefined}
        className={`absolute inset-0 h-full w-full object-cover ${
          loading ? 'blur-xl scale-110' : ''
        }`}
      />
      {loading && (
        <>
          <div className="absolute inset-0 bg-surface-base/40" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-alt select-none">
              {t('squarebitEye.loadingLabel')}
            </span>
            <div className="relative h-[3px] w-40 md:w-56 overflow-hidden rounded-full bg-surface-base/70 backdrop-blur-sm">
              <div className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-blue animate-eye-load-slide motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-60" />
            </div>
          </div>
        </>
      )}
      {variant === 'static' && (
        <span className="absolute bottom-3 right-3 rounded-full border border-border-brand bg-surface-base/90 backdrop-blur-sm px-2.5 py-1 font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-alt select-none">
          {t('squarebitEye.staticBadge')}
        </span>
      )}
    </div>
  );
};

const SquarebitEyePage: React.FC = () => {
  // Free tier, claim on click. Mirrors the Subdivs free card: a logged-out
  // visitor signs up first with the intent remembered, and a logged-in one is
  // sent to My Products with ?getEye=1, which performs the claim and then asks
  // for the EULA before anything downloads.
  //
  // The claim itself is NOT gated on the EULA. The download already is, so
  // accepting there covers it, and a claim made before a EULA revision does
  // not escape the newer terms the way a claim-time-only acceptance would.
  const handleFreeClaim = () => {
    if (!isAuthenticated) {
      try {
        localStorage.setItem('claimEyeFree', '1');
      } catch {
        /* best effort: a blocked localStorage just means they click again */
      }
      navigate(ROUTER_LINKS.SIGNUP);
      return;
    }
    navigate(`${ROUTER_LINKS.MANAGEMENT}?getEye=1`);
  };
  const { t } = useTranslation();
  const [webgl] = useState(supportsWebGL);
  const [liveRef, liveInView] = useInViewOnce<HTMLElement>();
  // Pricing visibility: the section renders ONLY for signed-in accounts
  // holding the 'squarebit-eye' grant, while the Eye is sold under contract
  // and its pricing is unpublished. Everyone else gets the rest of the page —
  // it is ordinary marketing — and the final CTA sends them to Contact, which
  // is the intended way in while this holds.
  //
  // WHAT THIS IS NOT. It decides what to DRAW. The prices live in the locale
  // files that ship to every visitor, so this hides them from someone reading
  // the page, not from someone reading the bundle. The boundary that matters
  // is on the API: see claimFreeEye and the checkout session, which decide who
  // may actually obtain the product. Riser is the stronger pattern — its whole
  // page is served from an endpoint that 404s without the grant — and the Eye
  // does not use it because the Eye's page is deliberately public apart from
  // this one section.
  //
  // Shares the ['my-access'] query key with My Products and Settings, so
  // however many places ask, it is fetched once.
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const access = useProductAccess();
  // Hidden until we KNOW there is a grant: `isLoading` covers the window
  // before the session check returns, and defaulting to hidden means a
  // reload never flashes the prices at someone who may not have them.
  const showPricing = !access.isLoading && access.has(EYE_PRODUCT_SLUG);

  return (
    <div className="w-full px-6 md:px-10 py-16 md:py-24">
      {/* Hero */}
      <section>
        {EYE_VERSION && (
          <Link
            to={ROUTER_LINKS.SQUAREBIT_EYE_CHANGELOG}
            className="inline-flex items-center gap-[5px] mb-5 px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide cursor-pointer hover:bg-surface-hover-section transition-colors"
          >
            <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
            {t('management.product.version', { version: EYE_VERSION })}
          </Link>
        )}
        <h1 aria-label="Squarebit Eye" className="hero-lockup text-content-dark">
          <div className="w-fit">
            <div
              aria-hidden="true"
              className="flex items-center gap-[1em] font-display font-semibold uppercase text-content-muted-alt leading-none text-[clamp(10px,1.7vw,17px)] tracking-[0.42em] mb-[8px] md:mb-[12px]"
            >
              <span className="h-px flex-1 bg-current opacity-30 animate-hero-rule-l" />
              <span className="whitespace-nowrap -mr-[0.42em] animate-hero-eyebrow">
                Squarebit
              </span>
              <span className="h-px flex-1 bg-current opacity-30 animate-hero-rule-r" />
            </div>
            <span className="hero-wordmark block font-display font-extrabold uppercase leading-[0.9] tracking-[-0.045em] animate-hero-wordmark">
              Eye
            </span>
            <span className="block font-display font-semibold uppercase text-content-muted-alt mt-[8px] md:mt-[14px] pl-[4px] tracking-[0.34em] text-[clamp(14px,3.2vw,24px)] animate-hero-tagline">
              Cross-DCC
            </span>
          </div>
        </h1>
        <h2 className="font-display font-bold text-[20px] md:text-[26px] tracking-[-0.02em] leading-[1.3] text-content-muted-alt mt-6 max-w-[600px]">
          {t('squarebitEye.title')}
        </h2>
        <p className="text-content-muted-alt text-[16px] md:text-[18px] leading-[1.65] mt-5 max-w-[580px]">
          {t('squarebitEye.subtitle')}
        </p>
        <div className="flex items-center gap-x-6 gap-y-3 mt-8 flex-wrap">
          {/* Straight to the pricing cards, the way the Subdivs hero does.
              "Try it free" rather than "Contact us": there is a free tier
              now, so making someone email us before they can even look at it
              is a step that buys nobody anything. */}
          <button
            type="button"
            onClick={() =>
              document
                .getElementById('pricing')
                ?.scrollIntoView({ behavior: 'smooth' })
            }
            className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[15px] px-[26px] py-[13px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
          >
            {t('squarebitEye.heroCtaFree')}
          </button>
          <button
            type="button"
            onClick={() =>
              document
                .getElementById('live-eye')
                ?.scrollIntoView({ behavior: 'smooth' })
            }
            className="font-body text-[14px] font-medium text-blue hover:text-blue-deep-1 transition-colors"
          >
            {t('squarebitEye.heroSecondary')} ↓
          </button>
          <Link
            to={ROUTER_LINKS.SQUAREBIT_EYE_DOCS}
            className="font-body text-[14px] font-medium text-blue hover:text-blue-deep-1 transition-colors"
          >
            {t('common.readTheDocs')} →
          </Link>
        </div>
      </section>

      {/* Live eye */}
      <section id="live-eye" ref={liveRef} className="mt-14 md:mt-20 scroll-mt-24">
        {!webgl ? (
          <StillPreview variant="static" />
        ) : liveInView ? (
          <Suspense fallback={<StillPreview variant="loading" />}>
            <EyeWidget aspectRatio={ASPECT_RATIO} />
          </Suspense>
        ) : (
          <StillPreview />
        )}
        <p className="text-content-muted-alt text-[13px] md:text-[14px] italic text-center mt-4">
          {t('squarebitEye.liveCaption')}
        </p>
      </section>

      {/* What it does */}
      <section className="mt-20 md:mt-28">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {t('squarebitEye.whatTitle')}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[16px] leading-[1.6] max-w-[620px] mb-10">
          {t('squarebitEye.whatSubtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] md:gap-[20px]">
          {CAPABILITIES.map((c) => {
            const wide = c === WIDE_CARD;
            /* The art sits on its own fixed ground rather than the card's
               surface, the way the Standby tiles do it: the card is
               overflow-hidden, so the ground runs clean into the card edge and
               takes the side on a wide card or the top on a standard one.
               It also means the stills read identically in light and dark. */
            const media = (
              <div
                className="relative flex h-full w-full items-center justify-center overflow-hidden p-6"
                style={{
                  background: 'linear-gradient(160deg, #182226 0%, #0E1518 100%)'
                }}
              >
                <img
                  src={CAP_MEDIA[c].src}
                  alt={t(CAP_MEDIA[c].altKey)}
                  loading="lazy"
                  className="max-h-full max-w-full w-auto h-auto object-contain select-none pointer-events-none"
                />
              </div>
            );
            const copy = (
              <div className="flex flex-1 flex-col justify-center p-[24px] md:p-[28px]">
                <CardLabel>{t(`squarebitEye.cap_${c}_label`)}</CardLabel>
                <h3 className="font-display font-bold text-[18px] md:text-[20px] text-content-dark mb-[10px]">
                  {t(`squarebitEye.cap_${c}_title`)}
                </h3>
                <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
                  {t(`squarebitEye.cap_${c}_body`)}
                </p>
                <CapChips
                  items={Array.from({ length: CAP_CHIPS[c] }, (_, i) =>
                    t(`squarebitEye.cap_${c}_chip${i + 1}`)
                  )}
                />
              </div>
            );
            return (
              <div
                key={c}
                className={`relative flex overflow-hidden rounded-[16px] border border-border-brand bg-surface-base shadow-[0_2px_12px_rgba(34,211,238,0.06)] ${
                  wide ? 'flex-col md:col-span-2 md:flex-row' : 'flex-col'
                }`}
              >
                {wide ? (
                  <>
                    <div className="order-2 md:order-1 md:w-[46%]">{copy}</div>
                    <div className="order-1 h-[220px] shrink-0 md:order-2 md:h-auto md:w-[54%]">
                      {media}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h-[200px] shrink-0">{media}</div>
                    {copy}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* One shader, every DCC — the parity story */}
      <section className="mt-20 md:mt-28 rounded-[20px] bg-[var(--subscription-brand-100)] border border-border-brand px-[24px] md:px-[48px] py-[36px] md:py-[48px]">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {t('squarebitEye.parityTitle')}
        </h2>
        <p className="text-content-secondary text-[15px] md:text-[16px] leading-[1.7] max-w-[720px] mb-8">
          {t('squarebitEye.parityIntro')}
        </p>
        <ParitySheets />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-[28px]">
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-content-muted-alt mb-[8px]">
              {t('squarebitEye.parityLeftTitle')}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {t('squarebitEye.parityLeftBody')}
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-blue mb-[8px]">
              {t('squarebitEye.parityRightTitle')}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {t('squarebitEye.parityRightBody')}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing / licensing — grant-gated (see the visibility comment above).
          Three tiers mirroring the Subdivs pricing cards: Personal $0 (free
          for non-commercial use), Commercial $15 one-time per seat, Studio
          custom.

          Commercial is a real Stripe checkout (one-time, mode:'payment') with
          a seat picker; Personal and Studio remain plain links, into the
          access-grant flow and Contact respectively. Because the card renders
          every customPrice verbatim - it carries its own symbol - so all
          three read "$0" / "$15" / "Custom", and a card can be switched
          between checkout and contact without editing nine locale files.
          (It used to be the other way round, which is worth not re-deriving:
          the prefix keyed off checkout-vs-link mode, so one "$15" rendered
          as "$15" on a contact card and "$$15" on a checkout card.) */}
      {showPricing && (
        <section id="pricing" className="mt-20 md:mt-28 scroll-mt-24">
          <h2 className="font-display font-extrabold text-[28px] md:text-[40px] tracking-[-0.02em] text-content-dark text-center mb-3">
            {t('squarebitEye.pricingTitle')}
          </h2>
          <p className="text-content-muted-alt text-[15px] md:text-[16px] leading-[1.6] text-center max-w-[560px] mx-auto mb-10">
            {t('squarebitEye.pricingSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[14px] max-w-[1020px] mx-auto items-stretch">
            {/* Personal: free non-commercial tier. contactHref keeps the card
              in "plain link" mode — no price fetch, no consent panel, and
              the "$0" price renders verbatim. */}
            <SubscriptionPlan
              caption={t('squarebitEye.personalCaption')}
              customPrice={t('squarebitEye.personalPrice')}
              duration={t('squarebitEye.personalDuration')}
              plan="perpetual"
              product="squarebit-eye"
              features={
                t('squarebitEye.personalFeatures', {
                  returnObjects: true
                }) as string[]
              }
              finePrint={t('squarebitEye.personalFinePrint')}
              buttonText={t('squarebitEye.personalButton')}
              onCta={handleFreeClaim}
            />
            {/* Commercial: $15 one-time per seat, self-serve through Stripe
              (mode:'payment'). seatPicker sends the chosen count as the
              line-item quantity, which the checkout.session.completed
              webhook reads straight back out into the issued licence's seat
              count — so the number picked here IS the number of machines the
              licence activates.

              NOTE the fine print still reads "Contact us to purchase", which
              predates this card being self-serve and now contradicts the
              button beside it. Copy fix, not a code one, but it is the first
              thing a buyer reads. Badge mirrors the Subdivs Perpetual
              card's ribbon. */}
            <SubscriptionPlan
              caption={t('squarebitEye.commercialCaption')}
              customPrice={t('squarebitEye.commercialPrice')}
              duration={t('squarebitEye.commercialDuration')}
              plan="perpetual"
              product="squarebit-eye"
              badgeText={t('squarebitEye.commercialBadge')}
              features={
                t('squarebitEye.commercialFeatures', {
                  returnObjects: true
                }) as string[]
              }
              finePrint={t('squarebitEye.commercialFinePrint')}
              buttonText={t('squarebitEye.commercialButton')}
              seatPicker
              isPopular
            />
            {/* Studio: custom pricing, contact sales — mirrors the Subdivs
              Studio card. plan is required by the card props but unused in
              contact mode. */}
            <SubscriptionPlan
              caption={t('squarebitEye.studioCaption')}
              customPrice={t('squarebitEye.studioPrice')}
              duration={t('squarebitEye.studioDuration')}
              plan="perpetual"
              product="squarebit-eye"
              features={
                t('squarebitEye.studioFeatures', {
                  returnObjects: true
                }) as string[]
              }
              finePrint={t('squarebitEye.studioFinePrint')}
              buttonText={t('squarebitEye.studioButton')}
              contactHref={ROUTER_LINKS.CONTACT}
            />
          </div>
          {/* Platform, stated where someone decides to buy. Every compiled
            delivery here is a Windows build - the Maya plug-in is a .mll and
            the Unreal plugin targets Win64 - and nothing on this page said
            so, which leaves a Mac customer to find out after paying. */}
          <p className="text-content-muted-alt text-[12.5px] leading-[1.6] text-center max-w-[560px] mx-auto mt-6 font-medium">
            {t('squarebitEye.pricingPlatformNote')}
          </p>
          <p className="text-content-muted-alt text-[12.5px] leading-[1.6] text-center max-w-[560px] mx-auto mt-2">
            <Trans
              i18nKey="squarebitEye.pricingEulaNotice"
              components={[
                <Link
                  to="/squarebit-eye/eula"
                  className="text-blue underline hover:text-blue-deep-1"
                >
                  Squarebit Eye End User License Agreement
                </Link>
              ]}
            />
          </p>
        </section>
      )}

      {/* Final CTA */}
      <section className="mt-20 md:mt-28 mb-4 text-center">
        <h2 className="font-display font-extrabold text-[28px] md:text-[40px] tracking-[-0.02em] text-content-dark mb-4">
          {t('squarebitEye.ctaTitle')}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[17px] leading-[1.6] max-w-[560px] mx-auto mb-8">
          {t('squarebitEye.ctaBody')}
        </p>
        <Link
          to={ROUTER_LINKS.CONTACT}
          className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[16px] px-[30px] py-[14px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
        >
          {t('squarebitEye.ctaButton')}
        </Link>
      </section>
    </div>
  );
};

export default SquarebitEyePage;
